import AddToCartFormik from '@jetshop/core/cart/AddToCartFormik';
import t from '@jetshop/intl';
import { useStockStatus } from '@jetshop/ui/Stock/StockStatusContainer';
import { Form } from 'formik';
import { styled } from 'linaria/react';
import React, { useState } from 'react';
import addToCartMutation from '../../Cart/queries/addToCart.gql';
import CartQuery from '../../Cart/queries/CartQuery.gql';
import InputWithLabel from '../../Forms/Input';
import { theme } from '../../Theme';
import Button from '../../ui/Button';
import NotifyWhenBack from '../StockStatus/NotifyWhenBack';
import StockStatusIndicator from '../StockStatus/StockStatusIndicator';
import { useProductValidationMessage } from '../useProductValidationMessage';
import { VariantSelector } from '../VariantSelector';
import { useKlaviyoTracking } from '../../../hooks/useKlaviyoTracking';
import { useCart } from '@jetshop/core/components/Query/CartProvider';

const StyledForm = styled(Form)`
  display: flex;
  width: 100%;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  ${theme.below.md} {
    align-items: center;
  }

  button {
    border-color: black !important;
  }

  .quantity-input-wrapper {
    margin-right: 8px;
    input {
      width: 50px;
      height: 50px;
      appearance: textfield;
      text-align: center;
    }
  }
`;

const QuantityAddToCartWrapper = styled('div')`
  margin-top: 16px;
  display: flex;
  width: 100%;
  align-items: flex-end;
`;

const Error = styled('div')`
  margin-top: 1rem;
  color: #eb5757;
  font-size: 0.875rem;
`;

const animateCart = () => {
  const cartBadge = document.querySelector('.cart-badge');
  const cartSum = document.querySelector('.cart-sum');

  if (cartBadge) {
    cartBadge.classList.add('animate');
  }

  if (cartSum) {
    cartSum.classList.add('animate');
  }

  setTimeout(() => {
    if (cartBadge) {
      cartBadge.classList.remove('animate');
    }

    if (cartSum) {
      cartSum.classList.remove('animate');
    }
  }, 2000);
};

function AddToCartForm({
  product,
  variant,
  getMissingOptions,
  variantHandler,
  onQuantityChange
}) {
  const [showError, setShowError] = useState(false);
  const stockStatus = useStockStatus(variant || product);
  const outOfStockNotify = stockStatus.status === 'notifyWhenBack';
  const { validation: variantValidation, missingOptions } = variantHandler;
  const { hasVariants } = product;
  const { validationMessage } = useProductValidationMessage({
    missingOptions,
    variantValidation,
    hasVariants,
    stockStatus
  });
  const cart = useCart(CartQuery);

  const { addedToCart } = useKlaviyoTracking();

  const onAddToCartSuccess = async ({ quantity }) => {
    setShowError(false);

    const cartResponse = await cart.result.refetch();
    const updatedCart = cartResponse.data.cart;

    addedToCart(product, quantity, updatedCart);

    animateCart();
  };

  return (
    <AddToCartFormik
      onAddToCartSuccess={onAddToCartSuccess}
      onAddToCartError={() => setShowError(true)}
      cartQuery={CartQuery}
      cartMutation={addToCartMutation}
      product={product}
      variant={variant}
      getMissingOptions={getMissingOptions}
    >
      {() => {
        return (
          <>
            {product.hasVariants && (
              <VariantSelector
                product={product}
                variantHandler={variantHandler}
                showValidation={!!validationMessage}
              />
            )}
            <StyledForm
              onChange={data => onQuantityChange(parseInt(data.target.value))}
            >
              {product.customerComments &&
                product.customerComments.map(comment => (
                  <InputWithLabel
                    wrapperClassName="customer-comment-input-wrapper"
                    className="customer-comment-input"
                    data-testid={`${comment.name}-input`}
                    id={`comments['${comment.name}']`}
                    name={`comments['${comment.name}']`}
                    label={comment.name}
                    key={comment.name}
                  />
                ))}
              <StockStatusIndicator
                status={stockStatus.status}
                text={stockStatus.text}
              />
              {!outOfStockNotify && (
                <QuantityAddToCartWrapper>
                  <InputWithLabel
                    disableValidation
                    wrapperClassName="quantity-input-wrapper"
                    min="1"
                    type="number"
                    name="quantity"
                    label="Quantity"
                    showLabel={false}
                  />
                  <Button
                    data-testid="add-to-cart"
                    type="submit"
                    disabled={!!validationMessage}
                    style={{
                      opacity: !!validationMessage ? 1 : 1,
                      cursor: !!validationMessage ? 'not-allowed' : 'pointer',
                      width: '100%',
                      height: '50px'
                    }}
                  >
                    {validationMessage ? validationMessage : t('Add to cart')}
                  </Button>
                </QuantityAddToCartWrapper>
              )}
            </StyledForm>

            {showError && (
              <Error>{t('An error occured. Please try again!')}</Error>
            )}

            {outOfStockNotify && (
              <NotifyWhenBack
                articleNumber={(variant || product).articleNumber}
              />
            )}
          </>
        );
      }}
    </AddToCartFormik>
  );
}

export default AddToCartForm;
